import React, { useEffect, useState } from "react";
import { useTheme, THEMES } from "@/contexts/ThemeContext";
import { Moon, Sun, ArrowsHorizontal, Palette } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import NotificationBell from "./NotificationBell";
import { MobileNavDrawer } from "./MobileNav";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function TopBar({ title, subtitle, right, onOpenPalette }) {
  const { theme, setTheme, mode, toggleMode, rtl, setRtl } = useTheme();
  const { user } = useAuth();
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/tenant/branding").then(r => setBranding(r.data || {})).catch(() => {});
  }, [user?.tenant_id]);

  const logoUrl = branding?.logo?.url;

  return (
    <header className="sticky top-0 z-30 border-b border-[var(--border)] bg-[var(--bg)]/70 backdrop-blur-2xl">
      <div className="px-4 md:px-6 lg:px-8 h-16 flex items-center gap-3">
        <MobileNavDrawer onOpenPalette={onOpenPalette} />
        {logoUrl && (
          <img
            src={logoUrl}
            alt="Tenant logo"
            data-testid="topbar-tenant-logo"
            className="lg:hidden w-8 h-8 rounded-md object-contain bg-[var(--surface)] border border-[var(--border)]"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-[10px] md:text-xs text-[var(--text-3)] overline truncate">{subtitle || "Clariva"}</div>
          <h1 className="text-base md:text-lg lg:text-xl font-medium tracking-tight truncate" style={{ fontFamily: 'var(--font-heading)' }}>
            {title}
          </h1>
        </div>

        <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
          <div className="hidden md:flex items-center gap-2 flex-wrap justify-end">{right}</div>
          <button
            onClick={onOpenPalette}
            data-testid="topbar-command-palette-btn"
            className="hidden md:flex items-center gap-2 px-3 py-1.5 text-sm text-[var(--text-2)] glass-subtle hover:text-[var(--text)]"
          >
            Ask…
            <kbd className="font-mono text-xs px-1.5 py-0.5 rounded bg-[var(--surface-strong)] border border-[var(--border)]">⌘K</kbd>
          </button>

          <NotificationBell />

          <LanguageSwitcher compact />

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="theme-switcher-btn" aria-label="Change theme">
                <Palette size={18} />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-3 glass-strong border-0">
              <div className="overline mb-2">Theme Pack</div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {THEMES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTheme(t.id)}
                    data-testid={`theme-${t.id}`}
                    className={`text-left p-2 rounded-md border transition ${theme === t.id ? "border-[var(--accent)] bg-[var(--surface-2)]" : "border-[var(--border)] hover:border-[var(--border-strong)]"}`}
                  >
                    <div className="flex gap-1 mb-1">
                      <span className="w-4 h-4 rounded-sm border border-[var(--border)]" style={{ background: t.swatch }} />
                      <span className="w-4 h-4 rounded-sm" style={{ background: t.accent }} />
                    </div>
                    <div className="text-xs font-medium">{t.name}</div>
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between text-sm py-2 border-t border-[var(--border)]">
                <span className="text-[var(--text-2)]">Mode</span>
                <Button variant="ghost" size="sm" onClick={toggleMode} data-testid="toggle-mode-btn" className="gap-2">
                  {mode === "dark" ? <Sun size={14} /> : <Moon size={14} />}
                  {mode === "dark" ? "Light" : "Dark"}
                </Button>
              </div>
              <div className="flex items-center justify-between text-sm py-2 border-t border-[var(--border)]">
                <span className="text-[var(--text-2)]">Direction</span>
                <Button variant="ghost" size="sm" onClick={() => setRtl(!rtl)} data-testid="toggle-rtl-btn" className="gap-2">
                  <ArrowsHorizontal size={14} />
                  {rtl ? "RTL" : "LTR"}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      {right && (
        <div className="md:hidden px-4 pb-3 flex items-center gap-2 flex-wrap justify-end border-t border-[var(--border)] pt-3">
          {right}
        </div>
      )}
    </header>
  );
}
